import React from "react";
import PropTypes from "prop-types";

// Human-readable heading for each `group` value seeded by the backend.
const GROUP_LABELS = {
    recommendation_policy: "Recommendation Policy",
    underwriting: "Underwriting",
};

function groupLabel(key) {
    if (!key) {
        return "General";
    }
    return GROUP_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Groups a flat policy list by `group` in a stable order (matches the seed's
// display_order so the sidebar renders top-down the same way every reload).
function groupPolicies(policies) {
    const buckets = new Map();
    (policies || []).forEach(p => {
        const key = p.group || "";
        if (!buckets.has(key)) {
            buckets.set(key, []);
        }
        buckets.get(key).push(p);
    });
    // Sort each bucket by display_order then name; preserve bucket insertion
    // order (already sorted by group / display_order on the server).
    buckets.forEach(list => {
        list.sort((a, b) => {
            const ao = a.display_order == null ? 999 : a.display_order;
            const bo = b.display_order == null ? 999 : b.display_order;
            if (ao !== bo) {
                return ao - bo;
            }
            return (a.name || "").localeCompare(b.name || "");
        });
    });
    return Array.from(buckets.entries());
}

/**
 * Left-rail policy list. Read-only — parent owns selection state and passes
 * the currently active code so the corresponding item can highlight.
 */
const PolicyList = ({ policies, activeCode, onSelect }) => {
    const groups = groupPolicies(policies);
    return (
        <div className={"policy-hub__sidebar"}>
            <div className={"policy-hub__sidebar-title"}>Policy Hub</div>
            {groups.length === 0 ? (
                <div style={{ padding: "12px 20px", color: "#8a93a3", fontSize: 12 }}>
                    No policies configured yet.
                </div>
            ) : null}
            {groups.map(([groupKey, list]) => (
                <div key={groupKey || "general"} className={"policy-hub__sidebar-group"}>
                    <div className={"policy-hub__sidebar-group-label"}>
                        {groupLabel(groupKey)}
                    </div>
                    {list.map(p => (
                        <div
                            key={p.code}
                            className={
                                "policy-hub__sidebar-item" +
                                (activeCode === p.code
                                    ? " policy-hub__sidebar-item--active"
                                    : "")
                            }
                            onClick={() => onSelect && onSelect(p.code)}
                        >
                            <span className={"policy-hub__sidebar-item__name"}>
                                {p.name}
                            </span>
                            <span className={"policy-hub__sidebar-item__meta"}>
                                v{p.version} · {p.updated_by_label || "system"}
                            </span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

PolicyList.propTypes = {
    policies: PropTypes.array,
    activeCode: PropTypes.string,
    onSelect: PropTypes.func,
};

export default PolicyList;
